/**
 * Inspect-by-hand card: the window, when it was last checked, and the resulting
 * status.
 *
 * The status is the server's — `Inspection.status` arrives already decided by
 * the same `inspectStatus` rule the reminder job uses, so the card, the API and
 * the email can never disagree about whether something is overdue.
 *
 * "Mark checked" is a crew action (§2): the API gates it at `crew`, so an owner
 * sees it too, and a garage member with no role at all cannot exist. The button
 * is hidden rather than disabled when the caller cannot use it — a disabled
 * control invites a click and explains nothing.
 */

import { Check } from 'lucide-react';

import type { Inspection } from '@svcdue/contracts';

import { fmtShort } from '../lib/format';
import { Pill } from './primitives';

export function InspectCard({
  inspection,
  canLog,
  onMarkChecked,
  isPending,
}: {
  inspection: Inspection;
  canLog: boolean;
  onMarkChecked?: () => void;
  isPending?: boolean;
}) {
  const { status, daysUntil, nextDue, lastCheckedAt } = inspection;

  return (
    <div className={'insp2 insp2-' + status}>
      <div className="insp2-top">
        <div className="insp2-id">
          <b className="insp2-name">{inspection.itemName}</b>
          {inspection.partNumber && <span className="insp2-pn">{inspection.partNumber}</span>}
        </div>
        <Pill s={status} />
      </div>
      <div className="insp2-who">{inspection.who}</div>
      <div className="insp2-note">{inspection.note}</div>
      <div className="insp2-meta">
        <div>
          <span>Inspect every</span>
          <b>{inspection.windowDays} days</b>
        </div>
        <div>
          <span>Last checked</span>
          {/* A never-checked item is overdue by definition — nobody has ever
              looked at it — which is why "Never" pairs with a red pill. */}
          <b>{lastCheckedAt ? fmtShort(lastCheckedAt) : 'Never'}</b>
        </div>
        <div>
          <span>{status === 'over' ? 'Overdue' : 'Next due'}</span>
          <b className={status === 'over' ? 'c-red' : status === 'due' ? 'c-amber' : ''}>
            {status === 'over' ? Math.abs(daysUntil) + 'd ago' : fmtShort(nextDue) + ' · ' + daysUntil + 'd'}
          </b>
        </div>
      </div>
      {canLog && (
        <button className="act act-sm insp2-btn" onClick={onMarkChecked} disabled={isPending}>
          <Check size={11} /> {isPending ? 'Saving…' : 'Mark checked today'}
        </button>
      )}
    </div>
  );
}
